import { Close } from '@mui/icons-material';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ActionBubbleTooltip from '../ActionBubbleTooltip';
import {
    canShowPrinterFarmLinkSnapshot,
    PRINTER_SNAPSHOT_TOOLTIP,
    printerSnapshotCapturedTooltip,
    printerSnapshotUrl,
    resolvePrinterUuid,
} from '../../helpers/printerSnapshot';
import { loadPrinterSnapshot, PRINTER_SNAPSHOT_MAX_AGE_MS } from '../../helpers/printerSnapshotStorage';
import { LIVE_NOTIFICATION_KINDS, subscribeLiveNotifications } from '../../helpers/liveNotifications';
import Printer3dIcon from '../icons/Printer3dIcon';

const SIZES = {
    card: {
        width: '100%',
        aspectRatio: '16 / 9',
    },
    row: {
        width: 88,
        height: 50,
        flexShrink: 0,
        borderRadius: 1,
    },
};

const SNAPSHOT_REFRESH_MIN_MS = 150;
const SNAPSHOT_REFRESH_MAX_MS = 500;

function delay(ms) {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

function preloadImage(src) {
    return new Promise((resolve) => {
        if (!src) {
            resolve();
            return;
        }
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });
}

async function runSnapshotRefreshAnimation(dataUrl) {
    const startedAt = Date.now();
    await Promise.race([
        preloadImage(dataUrl),
        delay(SNAPSHOT_REFRESH_MAX_MS),
    ]);
    const elapsed = Date.now() - startedAt;
    await delay(Math.max(0, SNAPSHOT_REFRESH_MIN_MS - elapsed));
}

function PlaceholderContent() {
    return (
        <Printer3dIcon
            fontSize="large"
            sx={{ color: 'action.disabled', opacity: 0.55 }}
        />
    );
}

export default function PrinterSnapshotThumb({
    printer,
    src,
    alt = 'Snapshot da impressora',
    size = 'card',
    enablePreview = true,
    enableTooltip = true,
}) {
    const [allowed, setAllowed] = useState(() => canShowPrinterFarmLinkSnapshot());
    const [failed, setFailed] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const refreshGenerationRef = useRef(0);
    const printerUuid = resolvePrinterUuid(printer);
    const initialStored = loadPrinterSnapshot(printerUuid);
    const [snapshotSrc, setSnapshotSrc] = useState(() => (
        src || initialStored?.dataUrl || printerSnapshotUrl(printer)
    ));
    const [snapshotUpdatedAt, setSnapshotUpdatedAt] = useState(initialStored?.updatedAt || null);

    useEffect(() => {
        const syncAccess = () => setAllowed(canShowPrinterFarmLinkSnapshot());
        syncAccess();
        window.addEventListener('acompanha:user-updated', syncAccess);
        return () => window.removeEventListener('acompanha:user-updated', syncAccess);
    }, []);

    useEffect(() => {
        setFailed(false);
        const stored = loadPrinterSnapshot(printerUuid);
        setSnapshotSrc(src || stored?.dataUrl || printerSnapshotUrl(printer));
        setSnapshotUpdatedAt(stored?.updatedAt || null);
    }, [src, printerUuid, printer]);

    // A tela pode ficar aberta por horas sem receber foto nova, então a imagem
    // sai daqui assim que vence, sem depender de recarregar a página.
    useEffect(() => {
        if (!snapshotUpdatedAt) {
            return undefined;
        }

        const capturedAt = Date.parse(snapshotUpdatedAt);
        if (Number.isNaN(capturedAt)) {
            return undefined;
        }

        const remaining = capturedAt + PRINTER_SNAPSHOT_MAX_AGE_MS - Date.now();
        const timer = window.setTimeout(() => {
            setSnapshotSrc(src || printerSnapshotUrl(printer));
            setSnapshotUpdatedAt(null);
        }, Math.max(0, remaining));

        return () => window.clearTimeout(timer);
    }, [snapshotUpdatedAt, src, printer]);

    useEffect(() => {
        if (!printerUuid) {
            return undefined;
        }

        return subscribeLiveNotifications((detail) => {
            if (detail?.kind !== LIVE_NOTIFICATION_KINDS.PRINTER_SNAPSHOT_UPDATED) {
                return;
            }
            if (detail.printerUuid !== printerUuid) {
                return;
            }
            if (!detail.dataUrl) {
                return;
            }

            const dataUrl = detail.dataUrl;
            const updatedAt = detail.updatedAt || new Date().toISOString();
            const generation = refreshGenerationRef.current + 1;
            refreshGenerationRef.current = generation;

            setFailed(false);
            setRefreshing(true);

            runSnapshotRefreshAnimation(dataUrl).then(() => {
                if (refreshGenerationRef.current !== generation) {
                    return;
                }
                setSnapshotSrc(dataUrl);
                setSnapshotUpdatedAt(updatedAt);
                setRefreshing(false);
            });
        });
    }, [printerUuid]);

    if (!allowed) {
        return null;
    }

    const dimensions = SIZES[size] || SIZES.card;
    const showImage = Boolean(snapshotSrc) && !failed;
    const canOpenPreview = enablePreview && showImage && !refreshing;
    const tooltipTitle = showImage
        ? (printerSnapshotCapturedTooltip(snapshotUpdatedAt) || PRINTER_SNAPSHOT_TOOLTIP)
        : PRINTER_SNAPSHOT_TOOLTIP;
    const previewCaption = printerSnapshotCapturedTooltip(snapshotUpdatedAt);
    const printerName = String(printer?.name || printer?.printer_name || '').trim();

    const handleOpenPreview = (event) => {
        if (!canOpenPreview) {
            return;
        }
        event.stopPropagation();
        setPreviewOpen(true);
    };

    const handlePreviewKeyDown = (event) => {
        if (!canOpenPreview) {
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
            setPreviewOpen(true);
        }
    };

    const content = showImage ? (
        <Box
            component="img"
            src={snapshotSrc}
            alt={alt}
            draggable={false}
            onError={() => setFailed(true)}
            sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                pointerEvents: 'none',
            }}
        />
    ) : (
        <PlaceholderContent />
    );

    const snapshotBox = (
        <Box
            aria-label={showImage ? alt : 'Aguardando foto da câmera do Farm Link'}
            role={canOpenPreview ? 'button' : undefined}
            tabIndex={canOpenPreview ? 0 : undefined}
            onClick={canOpenPreview ? handleOpenPreview : undefined}
            onKeyDown={canOpenPreview ? handlePreviewKeyDown : undefined}
            sx={{
                ...dimensions,
                position: 'relative',
                flexShrink: 0,
                borderBottom: size === 'row' ? 0 : 1,
                border: size === 'row' ? 1 : 0,
                borderColor: 'divider',
                borderRadius: size === 'row' ? 1 : 0,
                bgcolor: 'action.hover',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: canOpenPreview ? 'pointer' : 'default',
                outline: 'none',
                pointerEvents: canOpenPreview ? 'auto' : 'none',
                '&:focus-visible': canOpenPreview ? {
                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
                } : undefined,
            }}
        >
            {content}
            {refreshing && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.35)',
                        pointerEvents: 'none',
                    }}
                >
                    <CircularProgress size={28} sx={{ color: 'common.white' }} />
                </Box>
            )}
        </Box>
    );

    return (
        <>
            {enableTooltip ? (
                <ActionBubbleTooltip
                    title={canOpenPreview ? `${tooltipTitle} Clique para ampliar.` : tooltipTitle}
                    placement="top"
                    enterTouchDelay={0}
                    leaveTouchDelay={4000}
                >
                    {snapshotBox}
                </ActionBubbleTooltip>
            ) : (
                snapshotBox
            )}

            {enablePreview && (
            <Dialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                maxWidth="lg"
                fullWidth
                aria-labelledby="printer-snapshot-preview-title"
            >
                <DialogTitle
                    id="printer-snapshot-preview-title"
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        pr: 1,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" component="span" fontWeight={700} noWrap display="block">
                            {printerName || 'Snapshot da impressora'}
                        </Typography>
                        {previewCaption && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {previewCaption}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        aria-label="Fechar visualização"
                        onClick={() => setPreviewOpen(false)}
                        sx={{ mt: -0.5 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 1, sm: 2 },
                        bgcolor: 'grey.900',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src={snapshotSrc}
                        alt={alt}
                        sx={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: { xs: '70vh', sm: '80vh' },
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                        }}
                    />
                </DialogContent>
            </Dialog>
            )}
        </>
    );
}
